/**
 * Route guard for a signed-in person. UI-only — the backend re-checks every
 * request (CLAUDE.md §5.3 — no client-trusted authorisation).
 *
 * It waits while MSAL starts up (ADR-0053). With the cache in sessionStorage
 * a refreshed page sees the account before MSAL has finished initialising:
 * redirecting then would bounce it to /login, and rendering the page then
 * would ask for a token MSAL cannot yet give, so /me fails and the page
 * reads as "no role".
 *
 * A tab with no account — opened from a signed-in tab, or a pasted link — is
 * not sent to /login first (ADR-0075): it asks Microsoft for a silent sign-in
 * and renders nothing while the redirect is under way. Only when that was
 * refused does the login page appear, with the page that was asked for as
 * `state.from`, so sign-in returns there (`returnTo.ts`, ADR-0070). The
 * silent redirect carries the page as MSAL `state`, and the guard forwards to
 * it when the tab comes back.
 */

import { useEffect, useState } from "react";
import type { PropsWithChildren } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import {
  clearReturnTarget,
  peekReturnTarget,
  silentSignInRefused,
  silentSignInStarted,
  startSilentSignIn,
} from "./silentSignIn";
import { useAuthConfig } from "./useAuthConfig";

export function RequireAuth({ children }: PropsWithChildren) {
  const { instance, accounts, inProgress } = useMsal();
  const config = useAuthConfig();
  const { pathname, search, hash } = useLocation();
  const from = `${pathname}${search}${hash}`;
  const signedIn = accounts.length > 0;
  // Set when the silent request could not even be made (no navigation
  // happened): the login page is the only way forward then.
  const [fallback, setFallback] = useState(false);

  const attempt =
    !signedIn &&
    inProgress === InteractionStatus.None &&
    !fallback &&
    !silentSignInRefused() &&
    !silentSignInStarted();

  useEffect(() => {
    if (!attempt) return;
    let alive = true;
    void startSilentSignIn(instance, config.scopes, from).then(() => {
      if (alive && silentSignInRefused()) setFallback(true);
    });
    return () => {
      alive = false;
    };
  }, [attempt, instance, config.scopes, from]);

  if (inProgress === InteractionStatus.Startup) return null;
  if (!signedIn) {
    if (fallback || silentSignInRefused()) {
      return <Navigate to="/login" replace state={{ from }} />;
    }
    // Silent sign-in starting or under way; the tab is about to leave.
    return null;
  }
  const target = peekReturnTarget();
  if (target && target !== from) {
    return <Navigate to={target} replace />;
  }
  if (target) clearReturnTarget();
  return <>{children}</>;
}
